import React from 'react';
import { Box, Card, Typography, List, ListItem, ListItemText, Avatar, Button, Chip, CircularProgress } from '@mui/material';
import { EmptyState } from '@components/DataTable/components/EmptyState';
import { useNavigate } from 'react-router-dom';

interface JobApplication {
    _id: string;
    status: string;
    subStatus?: string;
    date: string;
    jobNumber: string;
    jobApplicationNumber: string;
    talent: {
        _id: string;
        fullName: string;
        profilePicture?: {
            url: string;
        };
    };
    jobOffer: {
        _id: string;
        title: string;
        recruitment?: {
            method: 'CLASSIC' | 'PREMIUM';
        };
    };
}

interface NewJobApplicationsProps {
    applications: JobApplication[];
    title: string;
    count?: number;
    loading?: boolean;
    onViewAll?: () => void;
}

export const NewJobApplications: React.FC<NewJobApplicationsProps> = ({
    applications = [],
    title,
    count = 0,
    loading,
    onViewAll,
}) => {
    const navigate = useNavigate();

    const handleItemClick = (app: JobApplication) => {
        navigate(`/applications?query=${encodeURIComponent(app.jobApplicationNumber)}`);
    };
    const formatDate = (date: string) => {
        const d = new Date(date);
        const now = new Date();
        const diff = now.getTime() - d.getTime();
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));

        if (days === 0) return 'Aujourd\'hui';
        if (days === 1) return 'Hier';
        return `il y a ${days} jours`;
    };

    const getStatusColor = (status: string) => {
        const statusMap: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success'> = {
            pending: 'warning',
            accepted: 'success',
            rejected: 'error',
            interested: 'info',
        };
        return statusMap[status?.toLowerCase()] || 'default';
    };

    return (
        <Card
            sx={{
                borderRadius: '16px',
                p: 2.5,
                height: '100%',
                bgcolor: theme => (theme.palette.mode === 'dark' ? '#1a1a2e' : '#fff'),
                border: '1px solid',
                borderColor: theme => (theme.palette.mode === 'dark' ? '#2d2d44' : '#e0e0e0'),
            }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                    <Typography sx={{ fontSize: '18px', fontWeight: 600 }}>{title}</Typography>
                </Box>
                <Button
                    onClick={onViewAll}
                    variant="text"
                    color="secondary"
                    sx={{ py: 0, px: 0.5, borderRadius: '4px', lineHeight: 1 }}
                    endIcon={<i className="fi fi-br-angle-small-right" style={{ fontSize: '16px' }} />}>
                    Voir tout
                </Button>
            </Box>

            <List sx={{ p: 0, maxHeight: '400px', overflow: 'auto' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={28} />
                    </Box>
                ) : applications.length > 0 ? (
                    applications.map((app) => (
                        <ListItem
                            key={app._id}
                            onClick={() => handleItemClick(app)}
                            sx={{
                                px: 0,
                                py: 1.5,
                                borderBottom: '1px solid',
                                borderColor: theme => theme.palette.mode === 'dark' ? '#2d2d44' : '#f0f0f0',
                                '&:last-child': { borderBottom: 'none' },
                                cursor: 'pointer',
                                '&:hover': {
                                    bgcolor: theme => theme.palette.mode === 'dark' ? '#252540' : '#f9f9f9',
                                },
                                alignItems: 'center',
                            }}>

                            {app.talent?.profilePicture?.url ? (
                                <Avatar
                                    src={app.talent.profilePicture.url}
                                    alt={app.talent.fullName}
                                    sx={{ 
                                        width: 40,
                                        height: 40,
                                        mr: 1.5,
                                        mt: 0.5,
                                    }}
                                />
                            ) : (
                                <Box
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        minWidth: 40,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: '#f0f0f0',
                                        borderRadius: '50%',
                                        mr: 1.5,
                                        mt: 0.5,
                                    }}>
                                    <i className="fi fi-br-briefcase" style={{ color: '#4239b0', fontSize: '18px' }} />
                                </Box>
                            )}
                            <ListItemText
                                primary={
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: 1 }}>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography sx={{ fontWeight: 500, fontSize: '14px' }}>
                                                {app.jobOffer?.title}
                                            </Typography>
                                            <Typography sx={{ fontSize: '12px', color: 'text.secondary', mt: 0.5 }}>
                                                {app.talent?.fullName}
                                            </Typography>
                                        </Box>
                                        <Chip
                                            label={app.status}
                                            size="small"
                                            color={getStatusColor(app.status)}
                                            sx={{ mt: -0.5 }}
                                        />
                                    </Box>
                                }
                                secondary={
                                    <Typography sx={{ fontSize: '11px', color: 'text.secondary', mt: 0.5 }}>
                                        {formatDate(app.date)}
                                    </Typography>
                                }
                            />
                            <i className="fi fi-br-angle-small-right" style={{ opacity: 0.5, fontSize: '14px', marginTop: 8 }} />
                        </ListItem>
                    ))
                ) : (
                    <EmptyState subtitle="Aucune nouvelle candidature" />
                )}
            </List>
        </Card>
    );
};
